import {
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  Layer,
  List,
  Menu,
  Select,
  Text,
  TextArea,
  TextInput
} from 'grommet'
import React, { useContext, useState } from 'react'
import { Constants, I18n } from 'galarm-config'
import GlobalConfig from '../GlobalConfig'
import { Add, Close, MoreVertical, Search } from 'grommet-icons'
import EditAlert from './EditAlert'
import { useSelector } from 'react-redux'
import ErrorProvider from './ErrorProvider'
import { isEmpty } from 'lodash'
import ViewApiCode from './ViewApiCode'
import AlertIncidents from './AlertIncidents'
import UserContext from './UserContext'
import WithRoles from './WithRoles'
import LabelPill from './LabelPill'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { logEvent } from 'firebase/analytics'
import AlertLogs from './AlertLogs'
import CloseIncidents from './CloseIncidents'
import {
  arrayRemove,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where
} from 'firebase/firestore'

const AlertSummary = ({
  item,
  index,
  onEditAlert,
  onDeleteAlert,
  onViewCode,
  onViewIncidents,
  onCloseIncidents,
  onTriggerAlert,
  onViewAlertLogs,
  userRoles,
  availableLabels
}) => {
  const [incidentNotes, setIncidentNotes] = useState('')
  const [incidentSummary, setIncidentSummary] = useState('')
  const [showIncidentNotes, setShowIncidentNotes] = useState(false)
  const displayIncidentNotes = () => {
    setShowIncidentNotes(true)
  }
  const hideIncidentNotes = () => {
    setShowIncidentNotes(false)
    setIncidentSummary('')
    setIncidentNotes('')
  }

  const menuItems = []
  if (
    Constants.ENTERPRISE_EDITOR_ROLES.some(role => userRoles.includes(role))
  ) {
    menuItems.push({ label: I18n.t('edit'), onClick: () => onEditAlert(item) })
    menuItems.push({
      label: I18n.t('delete'),
      onClick: () => onDeleteAlert(item)
    })
  }
  menuItems.push({
    label: I18n.t('viewLogs'),
    onClick: () => onViewAlertLogs(item)
  })
  menuItems.push({ label: I18n.t('viewCode'), onClick: () => onViewCode(item) })
  menuItems.push({
    label: I18n.t('viewIncidents'),
    onClick: () => onViewIncidents(item)
  })
  menuItems.push({
    label: I18n.t('closeIncidents'),
    onClick: () => onCloseIncidents(item)
  })
  if (
    Constants.ENTERPRISE_EDITOR_ROLES.some(role => userRoles.includes(role))
  ) {
    menuItems.push({
      label: I18n.t('triggerAlert'),
      onClick: () => triggerAlert()
    })
  }

  const triggerAlert = async () => {
    // Get message input from user and invoke the onTriggerAlert API
    displayIncidentNotes()
  }

  return (
    <Box
      direction="row"
      justify="between"
      align="center"
      background={index % 2 ? 'lightTint' : 'white'}
      pad="small">
      <Box direction="row" gap="xsmall" align="center">
        <Text margin="small">{item.name}</Text>
        {item.labels && item.labels.length > 0 && (
          <Box direction="row" gap="xsmall" wrap>
            {item.labels.map(labelId => {
              const label = availableLabels.find(l => l.id === labelId)
              return label ? <LabelPill key={labelId} label={label} /> : null
            })}
          </Box>
        )}
      </Box>
      <Menu
        dropBackground="lightTint"
        alignSelf="start"
        size="small"
        icon={<MoreVertical color="textColor" />}
        items={menuItems}
        dropAlign={{ top: 'bottom', right: 'right' }}
      />
      {showIncidentNotes && (
        <Layer
          background="lightTint"
          onEsc={hideIncidentNotes}
          onClickOutside={hideIncidentNotes}
          margin="medium">
          <Box margin="medium" width="medium">
            <Text>{I18n.t('incidentSummary')}</Text>
            <TextInput
              value={incidentSummary}
              onChange={event => setIncidentSummary(event.target.value)}
              placeholder={I18n.t('incidentSummaryPlaceholder')}
            />
            <Text margin={{ top: 'small' }}>{I18n.t('incidentNotes')}</Text>
            <Box height="small">
              <TextArea
                value={incidentNotes}
                onChange={event => setIncidentNotes(event.target.value)}
                placeholder={I18n.t('incidentNotesPlaceholder')}
                fill
              />
            </Box>
            <Box
              direction="row"
              gap="small"
              justify="end"
              margin="small"
              flex={{ shrink: 0 }}>
              <Button label={I18n.t('cancel')} onClick={hideIncidentNotes} />
              <Button
                primary
                label={I18n.t('triggerAlert')}
                style={{ color: 'white' }}
                onClick={() => {
                  onTriggerAlert(item.id, incidentSummary, incidentNotes)
                  hideIncidentNotes()
                }}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  )
}

const Alerts = ({ wizardMode = false }) => {
  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')
  const { user, userRoles } = useContext(UserContext)

  const isIncidentCoordinator = userRoles.includes(
    Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
  )

  const [searchText, setSearchText] = useState('')
  const [selectedMember, setSelectedMember] = useState(null)
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [selectedLabel, setSelectedLabel] = useState(null)

  const [showEditAlertDialog, setShowEditAlertDialog] = useState({})
  const displayAddAlertDialog = () =>
    setShowEditAlertDialog({
      isEdit: false,
      alert: {}
    })
  const displayEditAlertDialog = alert =>
    setShowEditAlertDialog({
      isEdit: true,
      alert
    })
  const hideEditAlertDialog = () => setShowEditAlertDialog({})

  const [showApiCodeDialog, setShowApiCodeDialog] = useState({})
  const displayApiCodeDialog = alert =>
    setShowApiCodeDialog({ alert, showIntroForNewAlert: false })
  const displayApiCodeDialogForNewAlert = alert =>
    setShowApiCodeDialog({ alert, showIntroForNewAlert: true })
  const hideApiCodeDialog = () => setShowApiCodeDialog({})

  const [viewIncidentsDialog, setViewIncidentsDialog] = useState({})
  const displayIncidentsDialog = alert => setViewIncidentsDialog({ alert })
  const hideIncidentsDialog = () => setViewIncidentsDialog({})

  const [closeIncidentsDialog, setCloseIncidentsDialog] = useState({})
  const displayCloseIncidentsDialog = alert =>
    setCloseIncidentsDialog({ alert })
  const hideCloseIncidentsDialog = () => setCloseIncidentsDialog({})

  const [viewAlertLogsDialog, setViewAlertLogsDialog] = useState({})
  const displayAlertLogsDialog = alert => setViewAlertLogsDialog({ alert })
  const hideAlertLogsDialog = () => setViewAlertLogsDialog({})

  const [errors, setErrors] = useState([])
  // eslint-disable-next-line no-unused-vars
  const addError = error => setErrors(errors.concat([error]))
  const onClearError = errorToClear => {
    const newErrors = errors.filter(error => error !== errorToClear)
    setErrors(newErrors)
  }

  const alerts = useSelector(state => state.accountInfo.alerts)
  const availableLabels = useSelector(state => state.accountInfo.labels)
  const members = useSelector(state => state.accountInfo.members)
  const teams = useSelector(state => state.accountInfo.teams)

  const currentMember = members.find(m => m.id === user?.uid)
  const effectiveListenerIds = isIncidentCoordinator
    ? [
        ...new Set([
          ...(currentMember?.assignedListeners || []),
          ...alerts
            .filter(alert =>
              (alert.labels || []).some(labelId =>
                (currentMember?.assignedLabels || []).includes(labelId)
              )
            )
            .map(alert => alert.id)
        ])
      ]
    : []

  const onEditAlert = alert => {
    const alertToEdit = {
      ...alert,
      integrations: Object.keys(alert.integrations || {}),
      labels: alert.labels || []
    }

    displayEditAlertDialog(alertToEdit)
  }

  const onViewCode = alert => {
    displayApiCodeDialog(alert)
  }

  const onViewIncidents = alert => {
    displayIncidentsDialog(alert)
  }

  const onCloseIncidents = alert => {
    displayCloseIncidentsDialog(alert)
  }

  const onViewAlertLogs = alert => {
    displayAlertLogsDialog(alert)
  }

  const onTriggerAlert = async (alertId, summary, incidentNotes) => {
    try {
      GlobalConfig.showProgress({
        state: Constants.ProgressStates.IN_PROGRESS,
        message: I18n.t('triggeringAlert'),
        closeable: false
      })

      const functions = getFunctions()
      const triggerAlertInternal = httpsCallable(
        functions,
        'triggerAlertInternal'
      )
      await triggerAlertInternal({
        event: {
          alertId: alertId,
          enterpriseAccountId,
          summary,
          message: `Triggered by ${user.displayName}
${incidentNotes}`
        }
      })

      GlobalConfig.hideProgress()

      logEvent(
        GlobalConfig.analytics,
        Constants.UserAnalyticsEvents.TRIGGER_ALERT_FROM_UI,
        {}
      )
    } catch (error) {
      console.error(error)
      addError(error.message)
      GlobalConfig.hideProgress()
    }
  }

  const onDeleteAlert = async alert => {
    try {
      GlobalConfig.showProgress({
        state: Constants.ProgressStates.IN_PROGRESS,
        message: I18n.t('deletingAlert'),
        closeable: false
      })

      // Mark the alert as inactive on Firestore
      await updateDoc(
        doc(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId,
          'alerts',
          alert.id
        ),
        {
          status: Constants.ENTERPRISE_ALERT_STATUSES.INACTIVE
        }
      )

      // Remove this listener from any incidentCoordinator members that have it assigned
      const coordinatorQuery = query(
        collection(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId,
          'members'
        ),
        where(
          'role',
          '==',
          Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
        ),
        where('assignedListeners', 'array-contains', alert.id)
      )
      const coordinatorSnapshot = await getDocs(coordinatorQuery)
      const coordinatorUpdates = coordinatorSnapshot.docs.map(coordinatorDoc =>
        updateDoc(coordinatorDoc.ref, {
          assignedListeners: arrayRemove(alert.id)
        })
      )
      await Promise.all(coordinatorUpdates)

      const deleteAlert = httpsCallable(getFunctions(), 'deleteAlert')
      await deleteAlert({
        enterpriseAccountId,
        alertId: alert.id
      })

      console.log('Added task to delete alert', alert.id)

      GlobalConfig.showProgress({
        state: Constants.ProgressStates.COMPLETE,
        message: I18n.t('addedTaskToDeleteAlert'),
        closeable: true
      })

      logEvent(
        GlobalConfig.analytics,
        Constants.UserAnalyticsEvents.DELETE_ALERT,
        {}
      )
    } catch (error) {
      console.error(error)
      addError(error.message)
      GlobalConfig.hideProgress()
    }
  }

  const sortedAlerts = alerts
    // case insensitive search
    .filter(alert => {
      if (isIncidentCoordinator) {
        return (
          effectiveListenerIds.includes(alert.id) &&
          alert.name.toLowerCase().includes(searchText.toLowerCase())
        )
      }
      const matchesSearch = alert.name
        .toLowerCase()
        .includes(searchText.toLowerCase())
      const matchesMember =
        !selectedMember ||
        (alert.assignees && alert.assignees.includes(selectedMember))
      const matchesTeam =
        !selectedTeam ||
        (alert.assignees && alert.assignees.includes(selectedTeam))
      const matchesLabel =
        !selectedLabel || (alert.labels && alert.labels.includes(selectedLabel))
      return matchesSearch && matchesMember && matchesTeam && matchesLabel
    })
    .sort((alert1, alert2) => alert1.name.localeCompare(alert2.name))

  if (wizardMode) {
    return (
      <ErrorProvider
        errors={errors}
        getErrorMessage={error => error}
        onClearError={onClearError}>
        <Box pad="small" gap="medium">
          <Text>{I18n.t('wizardAlertsDescription')}</Text>
          <WithRoles
            requiredRoles={Constants.ENTERPRISE_EDITOR_ROLES}
            assignedRoles={userRoles}>
            <Button
              primary
              alignSelf="start"
              icon={<Add color="white" />}
              label={I18n.t('listener')}
              style={{ color: 'white' }}
              onClick={displayAddAlertDialog}
            />
          </WithRoles>
        </Box>
        {!isEmpty(showEditAlertDialog) && (
          <Layer
            background="lightTint"
            onEsc={hideEditAlertDialog}
            onClickOutside={hideEditAlertDialog}
            margin="medium">
            <Box margin="medium">
              <EditAlert
                isEdit={showEditAlertDialog.isEdit}
                alert={showEditAlertDialog.alert}
                onClose={hideEditAlertDialog}
                displayApiCodeDialog={displayApiCodeDialogForNewAlert}
              />
              <Box style={{ position: 'absolute', top: 20, right: 20 }}>
                <Close onClick={hideEditAlertDialog} />
              </Box>
            </Box>
          </Layer>
        )}
      </ErrorProvider>
    )
  }

  return (
    <ErrorProvider
      errors={errors}
      getErrorMessage={error => error}
      onClearError={onClearError}>
      <Box pad="small" gap="small" width="large" flex={{ shrink: 0 }}>
        <Box direction="row" justify="between">
          <Box direction="row" align="center">
            <Heading margin="small" level={3}>
              {I18n.t('listeners')}
            </Heading>
            {alerts.length > 0 && (
              <TextInput
                icon={<Search />}
                placeholder={I18n.t('search')}
                value={searchText}
                onChange={event => setSearchText(event.target.value)}
              />
            )}
          </Box>
          <WithRoles
            requiredRoles={Constants.ENTERPRISE_EDITOR_ROLES}
            assignedRoles={userRoles}>
            <Button
              primary
              icon={<Add color="white" />}
              label={I18n.t('listener')}
              style={{ color: 'white' }}
              onClick={displayAddAlertDialog}
            />
          </WithRoles>
        </Box>
        {alerts.length > 0 && !isIncidentCoordinator && (
          <Box direction="row" gap="small" align="center">
            <Select
              placeholder={I18n.t('filterByMember')}
              value={selectedMember}
              options={members.map(m => ({
                label: `${m.firstName} ${m.lastName}`,
                value: m.id
              }))}
              labelKey="label"
              valueKey={{ key: 'value', reduce: true }}
              onChange={({ option }) =>
                setSelectedMember(option ? option.value : null)
              }
              clear
              onClear={() => setSelectedMember(null)}
            />
            <Select
              placeholder={I18n.t('filterByTeam')}
              value={selectedTeam}
              options={teams.map(t => ({ label: t.name, value: t.id }))}
              labelKey="label"
              valueKey={{ key: 'value', reduce: true }}
              onChange={({ option }) =>
                setSelectedTeam(option ? option.value : null)
              }
              clear
              onClear={() => setSelectedTeam(null)}
            />
            <Select
              placeholder={I18n.t('filterByLabel')}
              value={selectedLabel}
              options={availableLabels.map(l => ({
                label: l.name,
                value: l.id
              }))}
              labelKey="label"
              valueKey={{ key: 'value', reduce: true }}
              onChange={({ option }) =>
                setSelectedLabel(option ? option.value : null)
              }
              clear
              onClear={() => setSelectedLabel(null)}
            />
          </Box>
        )}
        <Card background="textBackgroundColor">
          <CardBody margin="none">
            {alerts.length === 0 ? (
              <Text margin="small" style={{ whiteSpace: 'pre-line' }}>
                {I18n.t('noAlertsConfiguredInEnterpriseAccount')}
              </Text>
            ) : (
              <List border={false} pad="none" margin="none" data={sortedAlerts}>
                {(item, index) => (
                  <AlertSummary
                    item={item}
                    index={index}
                    onEditAlert={onEditAlert}
                    onDeleteAlert={onDeleteAlert}
                    onViewCode={onViewCode}
                    onViewIncidents={onViewIncidents}
                    onTriggerAlert={onTriggerAlert}
                    onViewAlertLogs={onViewAlertLogs}
                    onCloseIncidents={onCloseIncidents}
                    userRoles={userRoles}
                    availableLabels={availableLabels}
                  />
                )}
              </List>
            )}
          </CardBody>
        </Card>
      </Box>
      {!isEmpty(showEditAlertDialog) && (
        <Layer
          background="lightTint"
          onEsc={hideEditAlertDialog}
          onClickOutside={hideEditAlertDialog}
          margin="medium">
          <Box margin="medium">
            <EditAlert
              isEdit={showEditAlertDialog.isEdit}
              alert={showEditAlertDialog.alert}
              onClose={hideEditAlertDialog}
              displayApiCodeDialog={displayApiCodeDialogForNewAlert}
            />
            <Box style={{ position: 'absolute', top: 20, right: 20 }}>
              <Close onClick={hideEditAlertDialog} />
            </Box>
          </Box>
        </Layer>
      )}
      {!isEmpty(showApiCodeDialog) && (
        <Layer
          background="lightTint"
          onEsc={hideApiCodeDialog}
          onClickOutside={hideApiCodeDialog}
          margin="medium">
          <Box margin="medium">
            <ViewApiCode
              alert={showApiCodeDialog.alert}
              showIntroForNewAlert={showApiCodeDialog.showIntroForNewAlert}
            />
            <Box style={{ position: 'absolute', top: 20, right: 20 }}>
              <Close onClick={hideApiCodeDialog} />
            </Box>
          </Box>
        </Layer>
      )}
      {!isEmpty(viewIncidentsDialog) && (
        <Layer
          background="lightTint"
          onEsc={hideIncidentsDialog}
          onClickOutside={hideIncidentsDialog}
          margin="medium">
          <Box margin="medium">
            <AlertIncidents
              alert={viewIncidentsDialog.alert}
              onClose={hideIncidentsDialog}
            />
            <Box style={{ position: 'absolute', top: 20, right: 20 }}>
              <Close onClick={hideIncidentsDialog} />
            </Box>
          </Box>
        </Layer>
      )}
      {!isEmpty(closeIncidentsDialog) && (
        <Layer
          background="lightTint"
          onEsc={hideCloseIncidentsDialog}
          onClickOutside={hideCloseIncidentsDialog}
          margin="medium">
          <Box margin="medium">
            <CloseIncidents
              alert={closeIncidentsDialog.alert}
              onClose={hideCloseIncidentsDialog}
            />
            <Box style={{ position: 'absolute', top: 20, right: 20 }}>
              <Close onClick={hideCloseIncidentsDialog} />
            </Box>
          </Box>
        </Layer>
      )}
      {!isEmpty(viewAlertLogsDialog) && (
        <Layer
          background="lightTint"
          onEsc={hideAlertLogsDialog}
          onClickOutside={hideAlertLogsDialog}
          margin="medium">
          <Box margin="medium">
            <AlertLogs
              alert={viewAlertLogsDialog.alert}
              onClose={hideAlertLogsDialog}
            />
            <Box style={{ position: 'absolute', top: 20, right: 20 }}>
              <Close onClick={hideAlertLogsDialog} />
            </Box>
          </Box>
        </Layer>
      )}
    </ErrorProvider>
  )
}

export default Alerts
